// Copyright (c) Microsoft. All rights reserved. Licensed under the MIT license.

import { Exception } from 'common/exceptions/Exception';

interface DialogResult {
  parse: boolean;
  value: unknown;
}

/**
 * Custom error type to handle API specific errors.
 */
export class DialogError extends Exception {
  constructor(message: string, public innerError?: unknown) {
    super(message, innerError);
  }
}

/**
 * An optimized size object computed based on Screen Height & Screen Width
 */
export interface IDialogSize {
  // Width in pixels
  width: number;

  // Width in percentage
  width$: number;

  // Height in pixels
  height: number;

  // Height in percentage
  height$: number;
}

export class Dialog<T> {
  /**
   * @constructor
   *
   * @param url Url to be opened in the dialog.
   * @param width Width of the dialog.
   * @param height Height of the dialog.
   */
  constructor(
    public url: string = location.origin,
    width = 1024,
    height = 768
  ) {
    if (!/^https/.test(url)) {
      throw new DialogError('URL has to be loaded over HTTPS.');
    }

    this.size = this._optimizeSize(width, height);
  }

  private _result: Promise<T>;
  get result(): Promise<T> {
    if (this._result == null) {
      this._result = this._addInDialog();
    }

    return this._result;
  }

  size: IDialogSize;

  private _addInDialog<T>(): Promise<T> {
    return new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(
        this.url,
        { width: this.size.width$, height: this.size.height$ },
        (result: Office.AsyncResult<Office.Dialog>) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(new DialogError(result.error.message, result.error));
          } else {
            const dialog = result.value;
            dialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              (args: { message: string; origin: string | undefined }) => {
                const result = this._safeParse(args.message) as T;
                resolve(result);
                dialog.close();
              }
            );

            dialog.addEventHandler(
              Office.EventType.DialogEventReceived,
              (args: { message?: string; error: number }) => {
                reject(new DialogError(args.message, args.error));
                dialog.close();
              }
            );
          }
        }
      );
    });
  }

  /**
   * Close any open dialog by providing an optional message.
   * If more than one dialogs are attempted to be opened
   * an exception will be created.
   */
  static close(message?: unknown) {
    let parse = false;
    let value = message;

    if (typeof message === 'function') {
      throw new DialogError(
        'Invalid message. Cannot pass functions as arguments'
      );
    } else if (!(value == null) && typeof value === 'object') {
      parse = true;
      value = JSON.stringify(value);
    }

    try {
      Office.context.ui.messageParent(
        JSON.stringify(<DialogResult>{ parse, value })
      );
    } catch (error) {
      throw new DialogError('Cannot close dialog', error);
    }
  }

  private _optimizeSize(
    desiredWidth: number,
    desiredHeight: number
  ): IDialogSize {
    const { width: screenWidth, height: screenHeight } = window.screen;

    const width = this._maxSize(desiredWidth, screenWidth);
    const height = this._maxSize(desiredHeight, screenHeight);
    const width$ = this._percentage(width, screenWidth);
    const height$ = this._percentage(height, screenHeight);

    return { width$, height$, width, height };
  }

  private _maxSize(value: number, max: number) {
    return value < max - 30 ? value : max - 30;
  }

  private _percentage(value: number, max: number) {
    return (value * 100) / max;
  }

  private _safeParse(data: string) {
    try {
      const result = JSON.parse(data) as DialogResult;
      if (result.parse === true) {
        return this._safeParse(result.value as string);
      }
      return result.value;
    } catch (_e) {
      return data;
    }
  }
}
